import { useState, useEffect } from "react";
import { Crown, Headphones, Bookmark, Ticket, Check, Loader2, Package } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StripeEmbeddedCheckout } from "./StripeEmbeddedCheckout";
import { useAuth } from "@/hooks/use-auth";
import { useNavigate } from "@tanstack/react-router";
import { track } from "@/lib/analytics";
import { planLimitLine } from "@/lib/plan-copy";
import { useCommercialConfig, usePaymentsAvailability } from "@/lib/pricing-labels";
import { DEFAULT_PRICING } from "@/lib/commercial-config";
import { annualSavingsPercent } from "@/lib/commercial-config";

type Props = {
  open: boolean;
  onClose: () => void;
  onUnlock?: () => void; // kept for compat (no-op; real activation comes from webhook)
};

type Plan = "monthly" | "yearly";

export function PremiumModal({ open, onClose }: Props) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const commercial = useCommercialConfig();
  const payments = usePaymentsAvailability();
  const pricing = commercial.pricing;
  const monthlyLabel = (pricing.premium_monthly ?? DEFAULT_PRICING.premium_monthly).label;
  const yearlyLabel = (pricing.premium_yearly ?? DEFAULT_PRICING.premium_yearly).label;
  const yearlySaving = annualSavingsPercent(
    pricing.premium_monthly?.amount ?? 399,
    pricing.premium_yearly?.amount ?? 2999,
  );
  const [plan, setPlan] = useState<Plan>("monthly");
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => { if (open) track("paywall_view", { surface: "premium_modal" }); }, [open]);

  const features = [
    { icon: <Bookmark className="h-4 w-4" />, label: `${planLimitLine("premium", commercial.limits, "pt")} — guardar sem limite` },
    { icon: <Headphones className="h-4 w-4" />, label: "Guia áudio mãos-livres" },
    { icon: <Ticket className="h-4 w-4" />, label: "Modos Carro & Transportes públicos" },
    { icon: <Crown className="h-4 w-4" />, label: "Restaurantes recomendados perto de ti" },
  ];

  const priceId = plan === "monthly" ? "premium_monthly" : "premium_yearly";

  function handleStart() {
    if (!payments.available) {
      alert(payments.reason ?? "Pagamentos indisponíveis neste ambiente.");
      return;
    }
    track("checkout_started", { plan, priceId, surface: "premium_modal" });
    if (!isAuthenticated) {
      onClose();
      navigate({ to: "/login" });
      return;
    }
    setCheckoutOpen(true);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setCheckoutOpen(false); } }}>
      <DialogContent className="max-w-2xl overflow-hidden p-0">
        {!checkoutOpen ? (
          <>
            <div className="relative px-6 pb-5 pt-7 text-brand-foreground" style={{ background: "var(--gradient-hero)" }}>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest opacity-90">
                <Crown className="h-4 w-4" /> Routify Premium
              </div>
              <DialogTitle className="mt-2 text-2xl font-semibold leading-tight">Desbloqueia tudo</DialogTitle>
              <DialogDescription className="mt-1 text-xs text-brand-foreground/90">
                Acesso imediato · Cancela quando quiseres
              </DialogDescription>
            </div>

            <div className="p-6">
              <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl bg-muted p-1">
                <button
                  type="button"
                  onClick={() => setPlan("monthly")}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition ${plan === "monthly" ? "bg-background text-foreground shadow" : "text-muted-foreground"}`}
                >
                  Mensal · {monthlyLabel}
                </button>
                <button
                  type="button"
                  onClick={() => setPlan("yearly")}
                  className={`relative rounded-lg px-3 py-2 text-sm font-medium transition ${plan === "yearly" ? "bg-background text-foreground shadow" : "text-muted-foreground"}`}
                >
                  Anual · {yearlyLabel}
                  {yearlySaving > 0 ? (
                    <span className="absolute -right-1 -top-2 rounded-full bg-brand px-1.5 py-0.5 text-[9px] font-bold text-brand-foreground">-{yearlySaving}%</span>
                  ) : null}
                </button>
              </div>

              <ul className="space-y-2.5">
                {features.map((f) => (
                  <li key={f.label} className="flex items-center gap-3 text-sm">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand/10 text-brand">{f.icon}</span>
                    <span className="font-medium">{f.label}</span>
                    <Check className="ml-auto h-4 w-4 text-brand" />
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={handleStart}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-brand-foreground transition hover:opacity-90"
                style={{ background: "var(--gradient-hero)" }}
              >
                <Crown className="h-4 w-4" />
                Subscrever Premium
              </button>
              <p className="mt-2 text-center text-[10px] text-muted-foreground">
                Acesso imediato após o pagamento. Cancela a qualquer momento.
              </p>

              <div className="mt-4 rounded-xl border border-border bg-muted/40 p-3 text-center">
                <p className="text-xs text-muted-foreground">Precisas só de algumas rotas extra?</p>
                <a
                  href="/credits"
                  onClick={onClose}
                  className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-brand hover:underline"
                >
                  <Package className="h-3.5 w-3.5" /> Ver packs de rotas (sem subscrição)
                </a>
              </div>
            </div>
          </>
        ) : (
          <div className="max-h-[85vh] overflow-y-auto">
            <DialogHeader className="sr-only">
              <DialogTitle>Finalizar subscrição Premium</DialogTitle>
              <DialogDescription>Conclui o pagamento seguro para ativar o Routify Premium.</DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 pr-12">
              <button
                type="button"
                onClick={() => setCheckoutOpen(false)}
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                ← Voltar
              </button>
              <span className="text-xs text-muted-foreground">
                {plan === "monthly" ? `${monthlyLabel}/mês` : `${yearlyLabel}/ano`}
              </span>
            </div>
            <div className="p-2">
              <StripeEmbeddedCheckout
                priceId={priceId}
                userId={user?.id}
                customerEmail={user?.email}
              />
            </div>
            <div className="flex items-center justify-center gap-2 border-t border-border p-3 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Após o pagamento serás redirecionado automaticamente.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
